import { createContext, useMemo, useReducer } from "react";

import { isEqual } from "lodash";

import type { Dispatch, ReactNode } from "react";

interface ShipmentFiltersProviderProps {
  children: ReactNode;
}

interface ShipmentFiltersContextState {
  regularFilters: ShipmentFilters | null;
  supplierFilters: ShipmentFilters | null;
  refreshCallbacks: {
    regular: (() => void) | null;
    supplier: (() => void) | null;
  };
}

interface SetFiltersAction {
  filters: ShipmentFilters;
  type: "setShipmentFilters";
  tabType?: "regular" | "supplier";
}

interface ClearFiltersAction {
  type: "clearShipmentFilters";
  tabType?: "regular" | "supplier";
}

interface SetOriginAction {
  type: "setOrigin";
  originKey: keyof ShipmentLocationFilter;
  value: string[];
  tabType?: "regular" | "supplier";
}

interface SetDestinationAction {
  type: "setDestination";
  destinationKey: keyof ShipmentLocationFilter;
  value: string[];
  tabType?: "regular" | "supplier";
}

interface SetEquipmentTypeAction {
  type: "setEquipmentType";
  equipments: string[];
  tabType?: "regular" | "supplier";
}

interface SetCommodityAction {
  type: "setCommodity";
  commodity: Commodity | null;
  tabType?: "regular" | "supplier";
}

interface SetShipmentStatusAction {
  type: "setShipmentStatus";
  status: ShipmentStatusValue[] | undefined;
  tabType?: "regular" | "supplier";
}

interface SetOrderByAction {
  type: "setOrderBy";
  orderBy: string;
  tabType?: "regular" | "supplier";
}

interface SetServiceAction {
  type: "setService";
  services: string[];
  tabType?: "regular" | "supplier";
}

interface SetShipmentVisibility {
  type: "setShipmentVisibility";
  visibility: ShipmentVisibility;
  tabType?: "regular" | "supplier";
}

interface SetShipmentRelationship {
  type: "setShipmentRelationship";
  shipmentRelation: ShipmentRelation;
  tabType?: "regular" | "supplier";
}

interface SetShipmentName {
  type: "setShipmentName";
  shipmentName: string;
  tabType?: "regular" | "supplier";
}

interface SetShipmentTypeAction {
  type: "setShipmentTypeAction";
  shipmentTypes: string[];
  tabType?: "regular" | "supplier";
}

interface SetIncludeBranchShipments {
  type: "SetIncludeBranchShipments";
  value: boolean;
  tabType?: "regular" | "supplier";
}

interface SetIncludeCompanyShipments {
  type: "SetIncludeCompanyShipments";
  value: boolean;
  tabType?: "regular" | "supplier";
}

interface SetBranches {
  type: "setBranches";
  branches: string[];
  tabType?: "regular" | "supplier";
}

interface SetShippers {
  type: "setShippers";
  shippers: string[];
  tabType?: "regular" | "supplier";
}

interface SetShipperBusinessType {
  type: "setShipperBusinessType";
  shipperBusinessType: BusinessTypeValue[] | null;
}

interface RegisterRefreshCallbackAction {
  type: "registerRefreshCallback";
  callback: () => void;
  tabType: "regular" | "supplier";
}

interface UnregisterRefreshCallbackAction {
  type: "unregisterRefreshCallback";
  tabType: "regular" | "supplier";
}

type ShipmentFiltersActions =
  | SetFiltersAction
  | ClearFiltersAction
  | SetOriginAction
  | SetDestinationAction
  | SetEquipmentTypeAction
  | SetCommodityAction
  | SetShipmentStatusAction
  | SetOrderByAction
  | SetServiceAction
  | SetShipmentVisibility
  | SetShipmentRelationship
  | SetShipmentName
  | SetShipmentTypeAction
  | SetIncludeBranchShipments
  | SetIncludeCompanyShipments
  | SetBranches
  | SetShippers
  | SetShipperBusinessType
  | RegisterRefreshCallbackAction
  | UnregisterRefreshCallbackAction;

interface ShipmentFiltersContextDispatch {
  dispatch: Dispatch<ShipmentFiltersActions>;
}

const initialState: ShipmentFiltersContextState = {
  regularFilters: {
    shipmentRelationship: "partner" as ShipmentRelation,
  },
  supplierFilters: null,
  refreshCallbacks: {
    regular: null,
    supplier: null,
  },
};

export const shipmentFiltersContext = createContext<
  ShipmentFiltersContextState & ShipmentFiltersContextDispatch
>({
  ...initialState,
  dispatch: () => null,
});

// Helper function to determine which filter state to update
function getFilterKey(
  tabType?: "regular" | "supplier",
): "regularFilters" | "supplierFilters" {
  return tabType === "supplier" ? "supplierFilters" : "regularFilters";
}

// Helper function to compare arrays for equality
function arraysEqual(a: string[] | undefined, b: string[]): boolean {
  const arrayA = a ?? [];
  return (
    arrayA.length === b.length && arrayA.every((val, index) => val === b[index])
  );
}

function reducer(
  state: ShipmentFiltersContextState,
  action: ShipmentFiltersActions,
): ShipmentFiltersContextState {
  switch (action.type) {
    case "setShipmentFilters": {
      const filterKey = getFilterKey(action.tabType);
      return {
        ...state,
        [filterKey]: action.filters,
      };
    }
    case "clearShipmentFilters": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];
      return {
        ...state,
        [filterKey]: {
          shipmentRelationship: currentFilters?.shipmentRelationship,
        },
      };
    }
    case "setOrigin": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      if (currentFilters?.origin) {
        return {
          ...state,
          [filterKey]: {
            ...currentFilters,
            origin: {
              ...currentFilters.origin,
              [action.originKey]: action.value,
            },
          },
        };
      }
      return {
        ...state,
        [filterKey]: {
          ...currentFilters,
          origin: {
            [action.originKey]: action.value,
          },
        },
      };
    }
    case "setDestination": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      if (currentFilters?.destination) {
        return {
          ...state,
          [filterKey]: {
            ...currentFilters,
            destination: {
              ...currentFilters.destination,
              [action.destinationKey]: action.value,
            },
          },
        };
      }
      return {
        ...state,
        [filterKey]: {
          ...currentFilters,
          destination: {
            [action.destinationKey]: action.value,
          },
        },
      };
    }

    case "setEquipmentType": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      // Early return if arrays are equal
      if (arraysEqual(currentFilters?.equipment_types, action.equipments)) {
        return state;
      }

      return {
        ...state,
        [filterKey]: {
          ...state[filterKey],
          equipment_types: action.equipments,
        },
      };
    }
    case "setShipmentTypeAction": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      // Early return if arrays are equal
      if (arraysEqual(currentFilters?.shipmentTypes, action.shipmentTypes)) {
        return state;
      }

      return {
        ...state,
        [filterKey]: {
          ...state[filterKey],
          shipmentTypes: action.shipmentTypes,
        },
      };
    }
    case "setCommodity": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      // Early return if commodity hasn't changed
      const currentCommodity = currentFilters?.commodity;
      const newCommodity = action.commodity ?? undefined;
      if (currentCommodity === newCommodity) {
        return state;
      }

      return {
        ...state,
        [filterKey]: {
          ...state[filterKey],
          commodity: newCommodity,
        },
      };
    }
    case "setShipmentStatus": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      // Early return if arrays are equal using deep comparison
      if (isEqual(currentFilters?.status, action.status)) {
        return state;
      }

      return {
        ...state,
        [filterKey]: {
          ...state[filterKey],
          status: action.status,
        },
      };
    }
    case "setOrderBy": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      // Early return if order_by hasn't changed
      if (currentFilters?.order_by === action.orderBy) {
        return state;
      }

      return {
        ...state,
        [filterKey]: {
          ...state[filterKey],
          order_by: action.orderBy,
        },
      };
    }
    case "setService": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      // Early return if arrays are equal
      if (arraysEqual(currentFilters?.services, action.services)) {
        return state;
      }

      return {
        ...state,
        [filterKey]: {
          ...state[filterKey],
          services: action.services,
        },
      };
    }
    case "setShipmentVisibility": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      // Early return if visibility hasn't changed
      if (currentFilters?.shipmentVisibility === action.visibility) {
        return state;
      }

      return {
        ...state,
        [filterKey]: {
          ...state[filterKey],
          shipmentVisibility: action.visibility,
        },
      };
    }
    case "setShipmentRelationship": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      // Early return if relationship hasn't changed
      if (currentFilters?.shipmentRelationship === action.shipmentRelation) {
        return state;
      }

      return {
        ...state,
        [filterKey]: {
          ...state[filterKey],
          shipmentRelationship: action.shipmentRelation,
        },
      };
    }
    case "setShipmentName": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      // Normalize values - treat undefined and empty string as the same
      const currentValue = currentFilters?.shipmentName ?? "";
      const newValue = action.shipmentName || "";

      // Early return if normalized values are the same
      if (currentValue === newValue) {
        return state;
      }

      return {
        ...state,
        [filterKey]: {
          ...state[filterKey],
          shipmentName: action.shipmentName,
        },
      };
    }
    case "SetIncludeBranchShipments": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      if (currentFilters) {
        return {
          ...state,
          [filterKey]: {
            ...currentFilters,
            includeBranchShipments: action.value,
            /*
            Clear branch and shipper filter selections when branch-wide
            shipments are selected such that filters do not conflict with
            each other.
            */
            branches: action.value ? [] : currentFilters.branches,
            shippers: action.value ? [] : currentFilters.shippers,
          },
        };
      }
      return {
        ...state,
        [filterKey]: {
          includeBranchShipments: action.value,
        },
      };
    }
    case "SetIncludeCompanyShipments": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      if (currentFilters) {
        return {
          ...state,
          [filterKey]: {
            ...currentFilters,
            includeCompanyShipments: action.value,
            /*
            Clear branch and shipper filter selections when company wide
            shipments are selected such that filters do not conflict with
            each other.
            */
            branches: action.value ? [] : currentFilters.branches,
            shippers: action.value ? [] : currentFilters.shippers,
          },
        };
      }
      return {
        ...state,
        [filterKey]: {
          includeCompanyShipments: action.value,
        },
      };
    }
    case "setBranches": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      if (currentFilters) {
        return {
          ...state,
          [filterKey]: {
            ...currentFilters,
            branches: action.branches,
            /*
            if any branches are specified, set includeCompanyShipments
            and includeBranchShipments are set to False
            clear any selected shippers
            such that the filters do not conflict with each other
            */
            includeCompanyShipments:
              action.branches.length > 0
                ? false
                : currentFilters.includeCompanyShipments,
            includeBranchShipments:
              action.branches.length > 0
                ? false
                : currentFilters.includeCompanyShipments,
            shippers: action.branches.length > 0 ? [] : currentFilters.shippers,
          },
        };
      }
      // if filters is not defined can only set branches, since there is no existing state
      // to evaluate
      return {
        ...state,
        [filterKey]: {
          branches: action.branches,
        },
      };
    }
    case "setShippers": {
      const filterKey = getFilterKey(action.tabType);
      const currentFilters = state[filterKey];

      if (currentFilters) {
        return {
          ...state,
          [filterKey]: {
            ...currentFilters,
            shippers: action.shippers,
            /*
            if any shippers are specified, set includeCompanyShipments
            and includeBranchShipments are set to False
            clear any selected shippers
            such that the filters do not conflict with each other
            In this case branches are not cleared as the shipper choice does not
            necessarily conflict with the branch. The backend will know to focus on the
            shipper even if a branch is specified in the request
            */
            includeCompanyShipments:
              action.shippers.length > 0
                ? false
                : currentFilters.includeCompanyShipments,
            includeBranchShipments:
              action.shippers.length > 0
                ? false
                : currentFilters.includeCompanyShipments,
          },
        };
      }
      return {
        ...state,
        [filterKey]: {
          shippers: action.shippers,
        },
      };
    }
    case "setShipperBusinessType": {
      const filterKey = getFilterKey("regular");
      const currentFilters = state[filterKey];

      if (currentFilters?.shipperBusinessType === action.shipperBusinessType) {
        return state;
      }

      return {
        ...state,
        [filterKey]: {
          ...state[filterKey],
          shipperBusinessType: action.shipperBusinessType,
        },
      };
    }
    case "registerRefreshCallback": {
      const callbackKey =
        action.tabType === "supplier" ? "supplier" : "regular";
      return {
        ...state,
        refreshCallbacks: {
          ...state.refreshCallbacks,
          [callbackKey]: action.callback,
        },
      };
    }
    case "unregisterRefreshCallback": {
      const callbackKey =
        action.tabType === "supplier" ? "supplier" : "regular";
      return {
        ...state,
        refreshCallbacks: {
          ...state.refreshCallbacks,
          [callbackKey]: null,
        },
      };
    }
    default:
      return state;
  }
}

export function ShipmentFiltersProvider(props: ShipmentFiltersProviderProps) {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const memoState = useMemo(() => ({ ...state, dispatch }), [state]);

  return (
    <shipmentFiltersContext.Provider value={memoState}>
      {children}
    </shipmentFiltersContext.Provider>
  );
}
